import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { FiChevronLeft, FiChevronRight, FiCheck, FiX, FiRotateCcw } from 'react-icons/fi';
import './CourseWizard.css';
import StepDetails from './StepDetails';
import StepTees from './StepTees';
import StepPars from './StepPars';
import ConfirmationModal from '../ConfirmationModal';
import { useAuth } from '../../AuthContext';
import { courseDraftKey, readCourseDraft, writeCourseDraft, clearCourseDraft } from './courseDraft';

const STEPS = [
  { key: 'details', label: 'Details', hint: 'Name & country' },
  { key: 'tees', label: 'Tees', hint: 'Ratings & slope' },
  { key: 'pars', label: 'Pars & SI', hint: 'Hole scorecard' },
];

let _keySeq = 0;
const nextKey = () => `new-${Date.now()}-${_keySeq++}`;

const emptyTee = () => ({
  _key: nextKey(),
  id: null,
  name: '',
  course_rating: '',
  slope_rating: '',
  hole_pars: Array(18).fill(''),
  hole_stroke_indices: Array(18).fill(''),
  // Yardage is kept in the unit the card PRINTS. Nothing converts it, here or
  // on the server: a metres card converted and converted back drifts.
  hole_lengths: Array(18).fill(''),
  length_unit: '',
});

// Map a course object into wizard tee state.
const teesFromCourse = (course) => {
  if (course?.tees?.length) {
    return course.tees.map(t => ({
      _key: String(t.id),
      id: t.id,
      name: t.name || '',
      course_rating: t.course_rating ?? '',
      slope_rating: t.slope_rating ?? '',
      hole_pars: t.hole_pars?.length === 18 ? [...t.hole_pars] : Array(18).fill(''),
      hole_stroke_indices: t.hole_stroke_indices?.length === 18 ? [...t.hole_stroke_indices] : Array(18).fill(''),
      hole_lengths: t.hole_lengths?.length === 18 ? t.hole_lengths.map(v => v ?? '') : Array(18).fill(''),
      length_unit: t.length_unit || '',
    }));
  }
  if (course) {
    // Legacy course-level scoring, no tees yet.
    return [{
      _key: 'default',
      id: null,
      name: 'Default',
      course_rating: course.course_rating ?? '',
      slope_rating: course.slope_rating ?? '',
      hole_pars: course.hole_pars?.length === 18 ? [...course.hole_pars] : Array(18).fill(''),
      hole_stroke_indices: course.hole_stroke_indices?.length === 18 ? [...course.hole_stroke_indices] : Array(18).fill(''),
      hole_lengths: Array(18).fill(''),
      length_unit: '',
    }];
  }
  return [emptyTee()];
};

const toSIArray = (arr) => arr.map(v => (v === '' || v === null || v === undefined) ? null : Number(v));
// An all-empty lengths row is sent as null, not eighteen nulls: the server
// stores a lengths array only when the card actually printed yardages.
const toLengthArray = (arr) => {
  const nums = arr.map(v => (v === '' || v === null || v === undefined) ? null : Number(v));
  return nums.some(n => n !== null) ? nums : null;
};
const toParArray = (arr) => arr.map(v => (v === '' || v === null || v === undefined) ? null : Number(v));

// Assess one tee's scorecard. Returns null when complete, otherwise a short,
// human description of what's still missing for that tee.
const teeScorecardIssue = (tee) => {
  const parsSet = tee.hole_pars.filter(p => Number(p) >= 3).length;
  const siVals = tee.hole_stroke_indices.map(Number).filter(n => n >= 1 && n <= 18);
  const siUnique = new Set(siVals);
  const started = parsSet > 0 || siVals.length > 0;

  // Untouched cards are fine to leave for later — only flag partial ones.
  if (!started) return null;

  const bits = [];
  if (parsSet < 18) bits.push(`${18 - parsSet} ${18 - parsSet === 1 ? 'par' : 'pars'}`);
  if (siUnique.size < 18) bits.push(`${18 - siUnique.size} stroke ${18 - siUnique.size === 1 ? 'index' : 'indexes'}`);
  if (bits.length === 0) return null;
  return bits.join(' and ');
};

/**
 * Course creation / editing wizard.
 * Props:
 *  - mode: 'create' | 'edit'
 *  - course: course object (edit only)
 *  - countries: array for the country select
 *  - authenticatedFetch
 *  - initialStep: 'details' | 'tees' | 'pars'
 *  - initialTeeId: tee id to pre-select on the Pars step (mid-round deep-link)
 *  - onClose(): dismiss without saving
 *  - onSaved(updatedCourse|null): saved successfully
 *
 * Everything typed here is also written to localStorage as it is typed — see
 * courseDraft.js for why — and restored on mount.
 *
 * The Global Directory's review screen is the SAME editor, which is why the
 * next few props exist: it writes to a different endpoint, carries a
 * confidence score, and offers Approve/Reject beside Save. Nothing about the
 * course itself differs, so nothing about the course editing does either —
 * the reviewer gets the cell picker, the SI conflict check and the draft that
 * the old bespoke review form never had.
 *  - title / headerExtra: header text and a node beneath it (the confidence bar)
 *  - submit(payload): replaces the built-in save; returns { ok, saved?, error? }
 *  - saveLabel / savedLabel: primary button text, and its text once done
 *  - extraActions: [{ key, label, className, onClick, confirm }] rendered
 *      before the primary button; `confirm` gates one behind the shared modal.
 *      The action owns what happens next (Reject closes the screen itself);
 *      the wizard only drops the draft and reports a failure it returns.
 *  - draftScope: namespace for the stored draft ('my' | 'global')
 */
const CourseWizard = ({
  mode = 'create',
  course = null,
  countries = [],
  authenticatedFetch,
  initialStep = 'details',
  initialTeeId = null,
  onClose,
  onSaved,
  title,
  headerExtra = null,
  submit = null,
  saveLabel,
  savedLabel = '✓ Saved',
  extraActions = [],
  draftScope = 'my',
}) => {
  const isEdit = mode === 'edit';

  const [name, setName] = useState(course?.name || '');
  const [country, setCountry] = useState(course?.country || '');
  const [address, setAddress] = useState(course?.address || '');
  const [postcode, setPostcode] = useState(course?.postcode || '');
  const [website, setWebsite] = useState(course?.website || '');
  const [tees, setTees] = useState(() => teesFromCourse(course));
  const [step, setStep] = useState(initialStep);
  const [activeTeeKey, setActiveTeeKey] = useState(() => {
    if (initialTeeId != null) {
      const match = tees.find(t => String(t.id) === String(initialTeeId));
      if (match) return match._key;
    }
    return tees[0]?._key;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [emptyWarning, setEmptyWarning] = useState('');
  // Ids of existing tees removed during this edit — deleted on save, since the
  // PUT endpoint upserts but never deletes tees absent from the payload.
  const [deletedTeeIds, setDeletedTeeIds] = useState([]);

  // ── Unsaved entry ─────────────────────────────────────────────
  // Everything typed here is written to localStorage as it is typed and read
  // back on mount — see courseDraft.js for why a course card in particular
  // cannot afford to live in React state alone.
  const { currentSociety } = useAuth();
  const draftKey = currentSociety?.id ? courseDraftKey(currentSociety.id, course?.id, draftScope) : null;

  // What counts as ENTRY. The step and the open tee are remembered too (below),
  // but walking between steps is not typing and must not leave a draft behind.
  const entry = useMemo(
    () => ({ name, country, address, postcode, website, tees, deletedTeeIds }),
    [name, country, address, postcode, website, tees, deletedTeeIds]
  );
  const pristineRef = useRef(null);
  if (pristineRef.current === null) pristineRef.current = JSON.stringify(entry);
  const isDirty = JSON.stringify(entry) !== pristineRef.current;

  const [draftReady, setDraftReady] = useState(false);
  const [restored, setRestored] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Set once the entry is safely on the server (or deliberately thrown away),
  // so the write below cannot resurrect a draft the wizard has finished with.
  const doneRef = useRef(false);

  const applyEntry = useCallback((d) => {
    setName(d.name ?? '');
    setCountry(d.country ?? '');
    setAddress(d.address ?? '');
    setPostcode(d.postcode ?? '');
    setWebsite(d.website ?? '');
    if (Array.isArray(d.tees) && d.tees.length > 0) setTees(d.tees);
    setDeletedTeeIds(Array.isArray(d.deletedTeeIds) ? d.deletedTeeIds : []);
  }, []);

  // Restore once, as soon as there is a society to key by.
  useEffect(() => {
    if (draftReady || !draftKey) return;
    const saved = readCourseDraft(draftKey);
    if (saved?.draft) {
      applyEntry(saved.draft);
      // Where the wizard was left off — unless the caller deep-linked to a
      // step or a tee (the mid-round par/SI fix), which is a specific request
      // and outranks where this entry was last put down.
      if (saved.draft.step && initialStep === 'details') setStep(saved.draft.step);
      if (saved.draft.activeTeeKey && initialTeeId == null) setActiveTeeKey(saved.draft.activeTeeKey);
      setRestored(true);
    }
    setDraftReady(true);
  }, [draftKey, draftReady, applyEntry, initialStep, initialTeeId]);

  // ...and write on every change afterwards. `draftReady` gates it because a
  // write on the first render would save the empty form over the very draft
  // the effect above is about to read.
  useEffect(() => {
    if (!draftKey || !draftReady || doneRef.current) return;
    if (isDirty) writeCourseDraft(draftKey, { ...entry, step, activeTeeKey });
    else clearCourseDraft(draftKey);
  }, [draftKey, draftReady, isDirty, entry, step, activeTeeKey]);

  // Put the form back the way it was found, and drop the draft with it.
  const discardDraft = useCallback(() => {
    applyEntry(JSON.parse(pristineRef.current));
    setRestored(false);
    clearCourseDraft(draftKey);
  }, [applyEntry, draftKey]);

  // Closing throws entry away, so it asks first — but only when there is
  // something to lose.
  const requestClose = () => {
    if (isDirty && !saveSuccess) { setConfirmDiscard(true); return; }
    doneRef.current = true;
    clearCourseDraft(draftKey);
    onClose && onClose();
  };

  const confirmDiscardAndClose = () => {
    doneRef.current = true;
    clearCourseDraft(draftKey);
    setConfirmDiscard(false);
    onClose && onClose();
  };

  // An embedder's own footer action, beside the primary one (the global
  // directory's Reject). `confirm` puts the shared modal in front of it, which
  // is what an irreversible one wants.
  const [pendingAction, setPendingAction] = useState(null);

  const runAction = async (action) => {
    setIsSaving(true);
    try {
      const result = await action.onClick();
      if (result && result.ok === false) {
        setIsSaving(false);
        setError(result.error || `${action.label} failed.`);
        return;
      }
      doneRef.current = true;
      clearCourseDraft(draftKey);
    } catch (e) {
      setIsSaving(false);
      setError(`${action.label} failed: ` + e.message);
    }
  };

  const requestAction = (action) => {
    if (action.confirm) { setPendingAction(action); return; }
    runAction(action);
  };

  // ── Tee mutations ─────────────────────────────────────────────
  const updateTee = useCallback((key, field, value) => {
    setTees(prev => prev.map(t => t._key === key ? { ...t, [field]: value } : t));
  }, []);

  const updateTeeHole = useCallback((key, field, holeIdx, value) => {
    setTees(prev => prev.map(t => {
      if (t._key !== key) return t;
      const arr = [...t[field]];
      arr[holeIdx] = value;
      return { ...t, [field]: arr };
    }));
  }, []);

  const addTee = useCallback(() => {
    setTees(prev => {
      const t = emptyTee();
      const first = prev[0];
      if (first) {
        // Carry over the card so a second tee starts from the same pars/SI.
        t.course_rating = first.course_rating;
        t.slope_rating = first.slope_rating;
        t.hole_pars = [...first.hole_pars];
        t.hole_stroke_indices = [...first.hole_stroke_indices];
      }
      return [...prev, t];
    });
  }, []);

  const removeTee = useCallback((key) => {
    setTees(prev => {
      if (prev.length <= 1) return prev;
      const removed = prev.find(t => t._key === key);
      if (removed?.id) setDeletedTeeIds(ids => [...ids, removed.id]);
      const next = prev.filter(t => t._key !== key);
      setActiveTeeKey(cur => (cur === key ? next[0]._key : cur));
      return next;
    });
  }, []);

  const copyHolesFromTee = useCallback((targetKey, sourceKey) => {
    setTees(prev => {
      const src = prev.find(t => t._key === sourceKey);
      if (!src) return prev;
      return prev.map(t => t._key === targetKey
        ? { ...t, hole_pars: [...src.hole_pars], hole_stroke_indices: [...src.hole_stroke_indices] }
        : t);
    });
  }, []);

  const applyHolesToAll = useCallback((sourceKey) => {
    setTees(prev => {
      const src = prev.find(t => t._key === sourceKey);
      if (!src) return prev;
      return prev.map(t => t._key === sourceKey
        ? t
        : { ...t, hole_pars: [...src.hole_pars], hole_stroke_indices: [...src.hole_stroke_indices] });
    });
  }, []);

  // ── Validation ────────────────────────────────────────────────
  const detailsValid = name.trim().length > 0;
  const teesValid = tees.length > 0 && tees.every(t => t.name.trim().length > 0);

  const allCardsComplete = useMemo(
    () => tees.every(t => teeScorecardIssue(t) === null && t.hole_pars.filter(p => Number(p) >= 3).length === 18),
    [tees]
  );
  const stepState = useMemo(() => ({
    details: detailsValid ? 'complete' : 'incomplete',
    tees: teesValid ? 'complete' : 'incomplete',
    pars: allCardsComplete ? 'complete' : 'optional',
  }), [detailsValid, teesValid, allCardsComplete]);

  const canSave = detailsValid && teesValid && !isSaving;

  // ── Step navigation ───────────────────────────────────────────
  const stepIndex = STEPS.findIndex(s => s.key === step);
  const goNext = () => { if (stepIndex < STEPS.length - 1) setStep(STEPS[stepIndex + 1].key); };
  const goBack = () => { if (stepIndex > 0) setStep(STEPS[stepIndex - 1].key); };

  const canGoToStep = (targetKey) => {
    if (isEdit) return true; // free navigation when editing
    const targetIdx = STEPS.findIndex(s => s.key === targetKey);
    if (targetIdx <= stepIndex) return true;
    // Forward only if all prior steps are valid.
    if (targetIdx >= 1 && !detailsValid) return false;
    if (targetIdx >= 2 && !teesValid) return false;
    return true;
  };

  // ── Save ──────────────────────────────────────────────────────
  // Everything a save sends, built from the current state. Shared with the
  // extra actions (Approve sends the reviewer's corrections before approving
  // them), so what is approved is always what is on screen.
  const buildPayload = () => {
    // Primary tee mirrors the course-level scoring fields. Keeping these in
    // the payload is what flags course-level (tee-less) rounds for
    // recalculation on the backend — do not drop them.
    const primary = tees.find(t => t._key === activeTeeKey) || tees[0];
    const teePayload = (t) => ({
      ...(t.id ? { id: t.id } : {}),
      name: t.name.trim(),
      course_rating: t.course_rating ? parseFloat(t.course_rating) : null,
      slope_rating: t.slope_rating ? parseFloat(t.slope_rating) : null,
      hole_pars: toParArray(t.hole_pars),
      hole_stroke_indices: toSIArray(t.hole_stroke_indices),
      hole_lengths: toLengthArray(t.hole_lengths),
      length_unit: t.length_unit || null,
    });
    return {
      name: name.trim(),
      country: country,
      address: address.trim() || null,
      postcode: postcode.trim() || null,
      website: website.trim() || null,
      course_rating: primary.course_rating ? parseFloat(primary.course_rating) : null,
      slope_rating: primary.slope_rating ? parseFloat(primary.slope_rating) : null,
      hole_pars: toParArray(primary.hole_pars),
      hole_stroke_indices: toSIArray(primary.hole_stroke_indices),
      tees: tees.map(teePayload),
    };
  };

  /**
   * The checks every write goes through, whether it is a save, an approval or
   * anything else an embedder puts in the footer. Returns false and explains
   * itself on screen when the entry is not fit to send.
   *  - `force` skips the one warning that is only a warning (empty scorecards).
   */
  const validateForWrite = (force = false) => {
    if (!canSave) {
      setError(!detailsValid ? 'Please enter a course name.' : 'Each tee needs a name.');
      setStep(!detailsValid ? 'details' : 'tees');
      return false;
    }

    // A started-but-unfinished scorecard would save broken hole data. These
    // always block — say exactly which tee needs what.
    const incomplete = tees
      .map(t => ({ key: t._key, name: t.name.trim() || 'Untitled', issue: teeScorecardIssue(t) }))
      .filter(t => t.issue);
    if (incomplete.length > 0) {
      setEmptyWarning('');
      setActiveTeeKey(incomplete[0].key);
      setStep('pars');
      setError(
        incomplete.length === 1
          ? `The ${incomplete[0].name} tee still needs ${incomplete[0].issue}. Finish its scorecard (or clear it) before saving.`
          : `Some tees still need finishing before you can save: ${incomplete.map(t => t.name).join(', ')}.`
      );
      return false;
    }

    // Fully-empty scorecards are allowed (finish later) but warned about once,
    // with a "save anyway" escape hatch.
    const empty = tees.filter(t => teeScorecardIssue(t) === null
      && t.hole_pars.filter(p => Number(p) >= 3).length === 0);
    if (!force && empty.length > 0) {
      setError('');
      setEmptyWarning(
        empty.length === tees.length
          ? "No scorecards have been entered yet — you can add pars & stroke indexes later."
          : `${empty.length} tee${empty.length === 1 ? '' : 's'} ha${empty.length === 1 ? 's' : 've'} no scorecard yet (${empty.map(t => t.name.trim() || 'Untitled').join(', ')}) — you can add them later.`
      );
      return false;
    }

    setError('');
    setEmptyWarning('');
    return true;
  };

  const handleSave = async (force = false) => {
    if (!validateForWrite(force)) return;
    setIsSaving(true);
    const payload = buildPayload();

    // An embedder that owns the endpoint (the global directory's review) does
    // its own write; everything after it — the draft, the success state — is
    // the same either way.
    if (submit) {
      try {
        const result = await submit(payload);
        if (!result?.ok) {
          setIsSaving(false);
          setError(result?.error || 'Save failed.');
          return;
        }
        doneRef.current = true;
        clearCourseDraft(draftKey);
        setSaveSuccess(true);
        setTimeout(() => { onSaved && onSaved(result.saved ?? null); }, 600);
      } catch (e) {
        setIsSaving(false);
        setError('Save failed: ' + e.message);
      }
      return;
    }

    try {
      // Delete removed tees first (PUT won't remove them). Abort on ANY failure
      // rather than half-saving and silently letting the "deleted" tee return.
      if (isEdit) {
        for (const tid of deletedTeeIds) {
          const del = await authenticatedFetch(`/courses/${course.id}/tees/${tid}`, { method: 'DELETE' });
          if (!del.ok) {
            const err = await del.json().catch(() => ({}));
            setIsSaving(false);
            setError(err.error || (del.status === 409
              ? 'A removed tee is still in use and could not be deleted.'
              : 'Could not delete a removed tee. Please try again.'));
            return;
          }
        }
      }

      const url = isEdit ? `/courses/${course.id}` : '/courses';
      const res = await authenticatedFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setIsSaving(false);
        setError(err.error || `Failed to ${isEdit ? 'save' : 'create'} course.`);
        return;
      }
      const saved = await res.json().catch(() => null);
      doneRef.current = true;
      clearCourseDraft(draftKey);
      setDeletedTeeIds([]);
      setSaveSuccess(true);
      setTimeout(() => { onSaved && onSaved(saved); }, 600);
    } catch (e) {
      setIsSaving(false);
      setError('Save failed: ' + e.message);
    }
  };

  return (
    <div className="cw-wrap">
      <div className="cw-header">
        <button className="cw-back" onClick={requestClose} disabled={isSaving} aria-label="Close">
          <FiX size={20} />
        </button>
        <h3 className="cw-title">{title || (isEdit ? 'Edit Course' : 'New Course')}</h3>
      </div>

      {headerExtra}

      {/* Stepper */}
      <div className="cw-stepper" role="tablist" aria-label="Course setup steps">
        {STEPS.map((s, i) => {
          const isActive = s.key === step;
          const enabled = canGoToStep(s.key);
          const state = stepState[s.key];
          // Fill the connector leading into this step once the previous
          // step is complete, so the rail visibly tracks progress.
          const linked = i > 0 && stepState[STEPS[i - 1].key] === 'complete';
          return (
            <button
              key={s.key}
              role="tab"
              aria-selected={isActive}
              className={[
                'cw-step',
                isActive ? 'cw-step--active' : '',
                enabled ? '' : 'cw-step--disabled',
                linked ? 'cw-step--linked' : '',
              ].join(' ').trim()}
              onClick={() => enabled && setStep(s.key)}
              disabled={!enabled}
            >
              <span className={`cw-step-dot cw-step-dot--${state}`}>
                {state === 'complete' && !isActive ? <FiCheck size={14} /> : i + 1}
              </span>
              <span className="cw-step-text">
                <span className="cw-step-label">{s.label}</span>
                <span className="cw-step-hint">{s.hint}</span>
              </span>
            </button>
          );
        })}
      </div>

      {restored && (
        <div className="cw-restored">
          <FiRotateCcw size={15} aria-hidden="true" />
          <span className="cw-restored-text">
            Picked up where you left off — nothing you typed was lost.
          </span>
          <button type="button" className="cw-restored-discard" onClick={discardDraft}>
            {isEdit ? 'Revert' : 'Start fresh'}
          </button>
        </div>
      )}

      {error && <div className="cw-error">{error}</div>}
      {emptyWarning && (
        <div className="cw-warn">
          <span>{emptyWarning}</span>
          <div className="cw-warn-actions">
            <button className="cw-warn-fix" onClick={() => { setEmptyWarning(''); setStep('pars'); }}>
              Add now
            </button>
            <button className="cw-warn-go" onClick={() => handleSave(true)} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save anyway'}
            </button>
          </div>
        </div>
      )}

      {/* Step body */}
      <div className="cw-body">
        {step === 'details' && (
          <StepDetails
            name={name} setName={setName}
            country={country} setCountry={setCountry}
            countries={countries}
            address={address} setAddress={setAddress}
            postcode={postcode} setPostcode={setPostcode}
            website={website} setWebsite={setWebsite}
            latitude={course?.latitude} longitude={course?.longitude}
            geoSource={course?.geo_source}
          />
        )}
        {step === 'tees' && (
          <StepTees
            tees={tees}
            updateTee={updateTee}
            addTee={addTee}
            removeTee={removeTee}
          />
        )}
        {step === 'pars' && (
          <StepPars
            tees={tees}
            activeTeeKey={activeTeeKey}
            setActiveTeeKey={setActiveTeeKey}
            updateTeeHole={updateTeeHole}
            updateTee={updateTee}
            copyHolesFromTee={copyHolesFromTee}
            applyHolesToAll={applyHolesToAll}
          />
        )}
      </div>

      {/* Sticky footer */}
      <div className={`cw-footer${extraActions.length > 0 ? ' cw-footer--crowded' : ''}`}>
        {stepIndex > 0 ? (
          <button className="cw-btn cw-btn--ghost" onClick={goBack} disabled={isSaving}>
            <FiChevronLeft size={18} /> Back
          </button>
        ) : (
          <button className="cw-btn cw-btn--ghost" onClick={requestClose} disabled={isSaving}>Cancel</button>
        )}

        {!isEdit && stepIndex < STEPS.length - 1 ? (
          <button
            className="cw-btn cw-btn--primary"
            onClick={goNext}
            disabled={(step === 'details' && !detailsValid) || (step === 'tees' && !teesValid)}
          >
            Next <FiChevronRight size={18} />
          </button>
        ) : (
          <>
            {extraActions.map(action => (
              <button
                key={action.key}
                className={`cw-btn ${action.className || 'cw-btn--ghost'}`}
                onClick={() => requestAction(action)}
                disabled={isSaving}
              >
                {action.label}
              </button>
            ))}
            <button className="cw-btn cw-btn--primary" onClick={() => handleSave()} disabled={!canSave}>
              {saveSuccess ? savedLabel : isSaving ? 'Saving…' : (saveLabel || (isEdit ? 'Update Course' : 'Save Course'))}
            </button>
          </>
        )}
      </div>

      <ConfirmationModal
        tone={pendingAction?.confirm?.tone || (pendingAction?.className?.includes('danger') ? 'danger' : 'neutral')}
        isOpen={!!pendingAction}
        title={pendingAction?.confirm?.title || ''}
        message={pendingAction?.confirm?.message || ''}
        confirmText={pendingAction?.confirm?.confirmText || pendingAction?.label || 'Confirm'}
        cancelText="Cancel"
        onConfirm={() => { const a = pendingAction; setPendingAction(null); runAction(a); }}
        onCancel={() => setPendingAction(null)}
      />

      <ConfirmationModal
        tone="danger"
        isOpen={confirmDiscard}
        title={isEdit ? 'Discard changes?' : 'Discard this course?'}
        message={
          isEdit
            ? 'Your unsaved changes to this course will be lost.'
            : 'Everything entered for this course will be lost.'
        }
        confirmText="Discard"
        cancelText="Keep editing"
        onConfirm={confirmDiscardAndClose}
        onCancel={() => setConfirmDiscard(false)}
      />
    </div>
  );
};

export default CourseWizard;
